// src/components/RecommendationCard.tsx
// Reusable recommendation card for Dashboard and result screens

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Recommendation } from '../lib/recommendations';

interface RecommendationCardProps {
  recommendation: Recommendation;
  variant?: 'compact' | 'full';
  showButton?: boolean;
  buttonText?: string;
}

export function RecommendationCard({
  recommendation,
  variant = 'full',
  showButton = true,
  buttonText = 'Jetzt machen',
}: RecommendationCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(recommendation.route);
  };

  if (variant === 'compact') {
    return (
      <button
        onClick={handleClick}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '14px 16px',
          background: 'rgba(255, 213, 79, 0.08)',
          border: '1px solid rgba(255, 213, 79, 0.2)',
          borderRadius: '12px',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: '24px' }}>{recommendation.emoji}</span>
        <div style={{ flex: 1 }}>
          <div style={{
            color: '#ffd54f',
            fontSize: '13px',
            fontWeight: 600,
            marginBottom: '2px',
          }}>
            Naechster Schritt
          </div>
          <div style={{
            color: '#e8eaf6',
            fontSize: '14px',
            lineHeight: 1.4,
          }}>
            {recommendation.text}
          </div>
        </div>
        <ChevronRight size={20} style={{ color: 'rgba(255, 213, 79, 0.6)' }} />
      </button>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(255, 213, 79, 0.12), rgba(255, 152, 0, 0.08))',
      border: '1px solid rgba(255, 213, 79, 0.25)',
      borderRadius: '16px',
      padding: '20px',
      marginTop: '16px',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
      }}>
        <span style={{ fontSize: '20px' }}>🎯</span>
        <span style={{
          color: '#ffd54f',
          fontSize: '14px',
          fontWeight: 700,
        }}>
          Empfohlener nächster Schritt
        </span>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        marginBottom: showButton ? '16px' : 0,
      }}>
        <span style={{ fontSize: '28px' }}>{recommendation.emoji}</span>
        <div style={{ flex: 1 }}>
          <div style={{
            color: '#e8eaf6',
            fontSize: '16px',
            lineHeight: 1.5,
          }}>
            {recommendation.text}
          </div>
          {recommendation.reason && (
            <div style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '13px',
              marginTop: '4px',
            }}>
              {recommendation.reason}
            </div>
          )}
        </div>
      </div>

      {showButton && (
        <button
          onClick={handleClick}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '14px',
            background: '#ffd54f',
            border: 'none',
            borderRadius: '12px',
            color: '#1a1a2e',
            fontSize: '15px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {buttonText}
          <ChevronRight size={18} />
        </button>
      )}
    </div>
  );
}

// Dashboard variant with course info
interface DashboardRecommendationCardProps {
  courseId: string;
  courseTitle: string;
  courseIcon: string;
  testCountdown?: { text: string; urgent: boolean };
  lastActivity?: { name: string; timeAgo: string; score?: number };
  recommendation: Recommendation;
}

export function DashboardRecommendationCard({
  courseId,
  courseTitle,
  courseIcon,
  testCountdown,
  lastActivity,
  recommendation,
}: DashboardRecommendationCardProps) {
  const navigate = useNavigate();

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(255, 213, 79, 0.1), rgba(255, 152, 0, 0.05))',
      border: '1px solid rgba(255, 213, 79, 0.2)',
      borderRadius: '16px',
      padding: '16px',
      marginBottom: '12px',
    }}>
      {/* Course header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '24px' }}>{courseIcon}</span>
          <span style={{ color: '#e8eaf6', fontSize: '15px', fontWeight: 600 }}>
            {courseTitle}
          </span>
        </div>
        {testCountdown && (
          <span style={{
            background: testCountdown.urgent
              ? 'rgba(239, 83, 80, 0.2)'
              : 'rgba(255, 255, 255, 0.1)',
            color: testCountdown.urgent ? '#ef5350' : 'rgba(255, 255, 255, 0.7)',
            padding: '4px 10px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 600,
          }}>
            {testCountdown.text}
          </span>
        )}
      </div>

      {/* Last activity */}
      {lastActivity && (
        <div style={{
          color: 'rgba(255, 255, 255, 0.5)',
          fontSize: '13px',
          marginBottom: '8px',
        }}>
          Zuletzt: {lastActivity.name}
          {lastActivity.score !== undefined && (
            <span style={{
              color: lastActivity.score >= 70 ? '#4caf50' : '#ff9800',
              marginLeft: '4px',
            }}>
              ({lastActivity.score}%)
            </span>
          )}
          <span style={{ marginLeft: '4px' }}>({lastActivity.timeAgo})</span>
        </div>
      )}

      {/* Recommendation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
      }}>
        <span style={{ fontSize: '18px' }}>{recommendation.emoji}</span>
        <span style={{ color: '#ffd54f', fontSize: '14px', fontWeight: 500 }}>
          {recommendation.text}
        </span>
      </div>

      {/* Action button */}
      <button
        onClick={() => navigate(recommendation.route)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          padding: '12px',
          background: 'rgba(255, 213, 79, 0.15)',
          border: '1px solid rgba(255, 213, 79, 0.3)',
          borderRadius: '10px',
          color: '#ffd54f',
          fontSize: '14px',
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Jetzt starten
        <ChevronRight size={16} />
      </button>
    </div>
  );
}
